import { isEqual, intersection } from 'lodash-es';
import axios from 'axios';
import { MARKS } from '@hotjar/webapp/performance';
import { mark, measureAndLog } from '@hotjar/performance';
import { SegmentViewedTracker } from '@hotjar/webapp/recordings/segment-viewed-tracker';
import { DEFAULT_SEGMENT, LEGACY_SEGMENT_TYPE } from '@hotjar/webapp/domains/segments/constant';
import { getRouteListNameFromUrl } from '../../../utils/getRouteListNameFromUrl';
import { summary } from '@hotjar/insights-common/lib/targeting-rules';
import { restDefaultHandlers } from '@hotjar/webapp/utils/rest-default-handlers';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { HotjarFilterOptionAll, HotjarFilterOptionWatched } from '../../../store/list/types';
import { getSiteId } from '../../../utils/getParamsFromUrlPathname';
import {
  flattenFiltersAndTagRepeated,
  setCurrentSegmentDateRange,
  isHotjarFiltersPreferencesInLocalStorage,
  isNotWatchedPopoverInfoSeen,
  extractDaysAgo,
} from './getPlaybacksUtils';
import { getUTCOffset } from './getUTCOffset';
import { getFiltersMergedWithRelevanceFilter } from '../../../utils/getFiltersMergedWithRelevanceFilter';

import type { QueryListFilter, QueryMapFilter } from '@hotjar/global-filters';
import type { Site } from '@hotjar/site';
import type { Recording } from '@hotjar/webapp/features/Playbacks/types';
import type { Store, JsonFilters } from '../../../store/list/types';
import { convertSortByV2ToV3 } from './convertSortByV2ToV3';
import type { EffectiveQuery } from './getPlaybacksUtils';

const API_ROOT_V3 = '/api/v3/sites/';

/**
 * Performs the retrieval of all the recordings associated with the current site that also match the current filters.
 *
 * Internally handles all the store I/O operations.
 *
 * @param store The Recording List central store.
 * @param isInitialLoad Whether it's the initial page load or not
 */
export const getPlaybacks = async (store: Store, isInitialLoad = false): Promise<Recording[]> => {
  measureAndLog({
    name: MARKS.RECORDINGS_LIST_FETCH_DELAY,
    startMark: MARKS.RECORDINGS_LIST_SEGMENTS_FETCH_TIME,
  });

  mark(MARKS.RECORDINGS_LIST_FETCH_START);

  const {
    site,
    jsonFilters,
    itemsPerPage,
    hotjarFilters,
    currentPage,
    currentSegment,
    sortBy,
    trackPageChange,
    previousPage,
    recordings,
    retrieveHighlightsCommentsAndLabels,
    retrieveTotalRecordingsCount,
    setAutomatedFilterUpdateFlag,
    setSearching,
    setEffectiveDateRangeFilter,
    setShowNotWatchedPopoverInfo,
    setJsonFilters,
    setRecordings,
    setRecordingsRequestTimeRange,
    setRecordingMode,
    setRecordingSettings,
    setRecordingsFetchedWithV3Api,
    setTotalHits,
    setTotalRecordingsCount,
    setTrackPageChange,
  } = store.getState();

  setRecordingsFetchedWithV3Api(true);

  /**
   * The relevance_score filter is a global filter. Since this is stored separately, we have to always merge it
   * into the global_filters object. In this case, in needs to happen before the isDirty check, otherwise the code
   * will incorrectly think that the global filters have not been changed, and thus send is_initial_load: true. If
   * this happens, it will instruct the BE to trigger logic specific to the initial load, overriding the filters and
   * thus losing the relevance filter.
   */
  const globalFilters = getFiltersMergedWithRelevanceFilter(
    jsonFilters as QueryListFilter,
    hotjarFilters,
  );

  const segment_id = SegmentViewedTracker.shouldTrackSegmentView ? currentSegment?.id : null;
  const isDirty = !isEqual(globalFilters, DEFAULT_SEGMENT.filters);

  setSearching(true);

  flattenFiltersAndTagRepeated(jsonFilters as JsonFilters);

  /* Retrieve the total count as a non-blocking side-effect. */
  retrieveTotalRecordingsCount();

  let response;

  try {
    response = (
      await axios.post(`${API_ROOT_V3}${getSiteId()}/recordings`, {
        global_filters: JSON.stringify(globalFilters),
        ...(hotjarFilters.watched !== HotjarFilterOptionAll && {
          /* If the "watched" local filter has been explicitly set, then send the explicit filter. */
          watched: hotjarFilters.watched === HotjarFilterOptionWatched,
        }),
        sort_by: convertSortByV2ToV3(sortBy),
        offset: itemsPerPage * (currentPage - 1),
        size: itemsPerPage,
        tz_offset: getUTCOffset(),
        is_initial_load: isInitialLoad && !isDirty,
        source: 'recordings-list',
        ...(segment_id && {
          segment_id,
          segment_type: LEGACY_SEGMENT_TYPE,
          state_name: getRouteListNameFromUrl() ?? '',
          tool: 'Recordings',
          url: window.location.href.replace(window.location.origin, ''),
        }),
      })
    ).data;
  } catch (e) {
    setRecordings([]);
    setSearching(false);
    setTotalHits(0);

    restDefaultHandlers.onError(response);

    /* When this request fails, none of the subsequent operations are needed and the function can safely return. */
    return [];
  }

  if (isInitialLoad) {
    let effectiveDateRange = null;

    if (response['effective_query']) {
      // On initial load, the backend decides whether to increase/decrease the date range
      // depending on the size of the result set - we reflect the effective query that the
      // backend returns in the filter pills and segments.
      const effectiveQuery = JSON.parse(response['effective_query']);
      effectiveDateRange = extractDaysAgo(effectiveQuery);
      if (effectiveDateRange != null) {
        setAutomatedFilterUpdateFlag(true);
        setJsonFilters(effectiveQuery);
        setCurrentSegmentDateRange(store, effectiveDateRange.toString());
      }
    }

    const effectiveDateRangeFilter = {
      DAYS_AGO: {
        created: effectiveDateRange ?? extractDaysAgo(jsonFilters as EffectiveQuery),
      },
    };

    setEffectiveDateRangeFilter(effectiveDateRangeFilter as QueryMapFilter);
  }
  SegmentViewedTracker.shouldTrackSegmentView = true;

  if (recordings.length === 0) {
    setRecordingSettings({
      excludeMinimalActivity: response.recording_exclude_minimal_activity,
      excludeShortRecordings: response.recording_exclude_short,
      recordingCaptureKeystrokes: response.recording_capture_keystrokes,
      recordingTargeting: summary(response.recording_targeting_rules),
    });

    setRecordingMode(response.recording_mode);

    if (
      !isHotjarFiltersPreferencesInLocalStorage() &&
      hotjarFilters.watched === 'not_watched' &&
      !isNotWatchedPopoverInfoSeen()
    ) {
      setShowNotWatchedPopoverInfo(true);
    }
  } else {
    if (trackPageChange) {
      const repeatedRecordings = intersection(
        recordings.map((x) => x.id),
        response.recordings.map((x: Recording) => x.id),
      );

      trackClick({
        name: 'pagination_button',
        uiPosition: 'recording_list_pagination',
        groupData: {
          account_id: (site as Site).account.id,
          organization_id: (site as Site).organizationId,
          site_id: (site as Site).id,
        },
        data: {
          current_page: currentPage,
          number_repeated_recordings: repeatedRecordings.length,
          previous_page: previousPage,
        },
      });

      setTrackPageChange(false);
    }
  }

  setRecordings(response.recordings);
  setTotalHits(response.total_hits);
  if (response.date_range && response.date_range.length === 2) {
    const [lowerBound, upperBound] = response.date_range;
    const currentTimestamp = Math.floor(Date.now() / 1000);

    setRecordingsRequestTimeRange([lowerBound, Math.min(upperBound, currentTimestamp)]);
  }

  /*
   * Only in case the total count hasn't been determined yet (e.g. by the early Quantz request), set it now using the
   * search hit count as a best-effort.
   *
   * This would make the recordings list potentially appear faster, since we don't necessarily need the full count
   * for that matter. This is fine because this count is not used for precise display purposes yet, but only to
   * control empty states or similar UI threshold-decisions.
   *
   * It reads the latest total count from the store, on the spot, after awaiting the main HTTP call.
   **/
  if (store.getState().totalRecordingsCount === 0) {
    setTotalRecordingsCount(response.total_hits);
  }

  // Perf: Measure how long it took to fetch the recordings list
  measureAndLog({
    name: MARKS.RECORDINGS_LIST_FETCH_TIME,
    startMark: MARKS.RECORDINGS_LIST_FETCH_START,
    addMark: true,
  });

  setSearching(false);

  /* Start retrieving Highlights as a non-blocking side effect after the main operation from above has concluded. */
  retrieveHighlightsCommentsAndLabels();

  return response.recordings;
};
